import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router';
import WhatsAppApiModal from '../../Meta/WhatsAppApiModal.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { channelInfoAtom } from '../../../Services/_states/index.js';
import { TemplateActions } from '../../../Services/_actions/Template/template.actions.js';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import RouterIcon from '@mui/icons-material/Router';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import { IconButton } from '@mui/material';

/** Normalize the mixed API shapes into a consistent object */
const normalizeApp = (item: any) => ({
  ...item,
  app_name:
    item?.app_name || item?.display_name || item?.source_name || 'WhatsApp App',
  wa_phone_number: item?.wa_phone_number || 'N/A',
  is_active:
    item?.is_active === true ||
    item?.status === 'active' ||
    item?.webhook?.registered === true,
  limit: item?.limit || 'N/A',
});

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');

const stringToHue = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
};

const breadcrumbsData = [
  {
    label: (
      <IconButton color="primary">
        <HomeIcon />
      </IconButton>
    ),
    href: '/',
  },
  {
    label: 'App List',
  },
];

const AppList: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const templateActions = TemplateActions();
  const setChannelInfo = useSetRecoilState(channelInfoAtom);

  const [channelList, setChannelList] = useState<any[]>([]);
  const [filteredList, setFilteredList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [isCreateAppModalOpen, setIsCreateAppModalOpen] = useState(false);
  const [appName, setAppName] = useState('');

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const res = await templateActions.getAllWabaAccounts();
      const raw: any[] = res || [];
      const normalized = raw.map(normalizeApp);
      setChannelList(normalized);
      setFilteredList(normalized);
    } catch (error: any) {
      showSnackBar(error?.message || 'Failed to fetch WhatsApp apps', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChannels(); }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFilteredList(
      q
        ? channelList.filter(
            (a) =>
              a.app_name.toLowerCase().includes(q) ||
              a.wa_phone_number?.includes(q) ||
              a.waba_id?.includes(q) ||
              a.company_name?.toLowerCase().includes(q)
          )
        : channelList
    );
  }, [search, channelList]);

  const handleGoLiveClick = (app: any) => { setSelectedApp(app); setShowWhatsAppModal(true); };
  const handleCloseWhatsAppModal = () => { setShowWhatsAppModal(false); setSelectedApp(null); };

  const handleCreateApp = () => {
    if (!appName.trim()) { showSnackBar('Please enter a valid app name', 'error'); return; }
    const newApp = normalizeApp({ app_name: appName, is_active: false, wa_phone_number: 'N/A', limit: 'N/A' });
    setChannelList((prev) => [...prev, newApp]);
    setIsCreateAppModalOpen(false);
    setAppName('');
  };

  const handleCardClick = (app: any) => {
    setChannelInfo(app);
    navigate('/r/whatsapp/manager/app-details/', { state: { app } });
  };

  const liveCount = channelList.filter((a) => a.is_active).length;

  return (
    <Box
      sx={{
        minHeight: '88vh',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #f8fafc 50%, #f0f9ff 100%)',
        // padding: { xs: '20px 14px', sm: '32px 28px' },
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      }}
    >
      {/* ── COMPACT HEADER ── */}
      <Box
        
      >
       
      {/* Header */}
      <Box mb={1}>
  <DynamicBreadcrumbs
    items={breadcrumbsData}
    onBreadcrumbClick={(event, item: { label: string; href: string }) => {
      if (item?.href) navigate(item.href);
    }}
  />
</Box>

<Box
  sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 1,
  }}
>
  <Typography variant="h4" fontWeight="bold">
    App List
  </Typography>

  <Button
    variant="contained"
    onClick={() => navigate('/r/setting/meta-embedded-signup')}
    sx={{
      textTransform: 'none', 
    }}
  >
  Create App
  </Button>
</Box>

        {/* Right: Actions */}
        {/* <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh apps">
            <Button
              onClick={fetchChannels}
              disabled={loading}
              sx={{
                minWidth: 36, padding: '5px 8px',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff', borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.25)',
                '&:hover': { background: 'rgba(255,255,255,0.25)' },
              }}
            >
              {loading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            </Button>
          </Tooltip>

          <Button
            startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => setIsCreateAppModalOpen(true)}
            sx={{
              background: 'rgba(255,255,255,0.18)',
              color: '#fff', border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: '8px', fontWeight: 700, textTransform: 'none',
              padding: '5px 14px', fontSize: '0.82rem',
              '&:hover': { background: 'rgba(255,255,255,0.28)' },
            }}
          >
            Create App
          </Button>
        </Box> */}
      </Box>

      {/* ── SEARCH ── */}
      {/* <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          background: '#fff', borderRadius: '14px',
          padding: '9px 16px', mb: '22px',
          border: '1.5px solid #e2e8f0',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}
      >
        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20, flexShrink: 0 }} />
        <input
          placeholder="Search by name, phone, WABA ID, company…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, border: 'none', outline: 'none',
            fontSize: '0.9rem', color: '#334155', background: 'transparent',
            fontFamily: 'inherit',
          }}
        />
        {search && (
          <Button size="small" onClick={() => setSearch('')}
            sx={{ color: '#94a3b8', minWidth: 0, p: 0, fontSize: '1rem', lineHeight: 1 }}>
            ✕
          </Button>
        )}
      </Box> */}

      {/* ── LOADING ── */}
      {loading && (
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={8}>
          <CircularProgress size={26} sx={{ color: 'primary.main' }} />
          <Typography color="textSecondary" fontSize="0.9rem">Loading apps…</Typography>
        </Box>
      )}

      {/* ── EMPTY ── */}
      {!loading && filteredList.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10, background: '#fff', borderRadius: '18px', border: '1.5px dashed #e2e8f0' }}>
          <WhatsAppIcon sx={{ fontSize: 52, color: '#d1fae5', mb: 1 }} />
          <Typography fontWeight={700} color="#64748b" fontSize="1rem">
            {search ? 'No apps match your search' : 'No WhatsApp apps found'}
          </Typography>
          <Typography fontSize="0.83rem" color="#94a3b8" mt={0.5}>
            {search ? 'Try a different keyword' : 'Click "+ Create App" to get started'}
          </Typography>
        </Box>
      )}

      {/* ── CARDS GRID ── */}
      <Box
        display="grid"
        gap={2.5}
        sx={{ gridTemplateColumns: 'repeat(auto-fill, minmax(285px, 1fr))' }}
      >
        {filteredList.map((app, index) => {
          const hue = stringToHue(app.app_name);
          return (
            <Box
              key={app?.wa_phone_number_id || index}
              onClick={() => handleCardClick(app)}
              sx={{
                background: app.is_active
                  ? 'linear-gradient(155deg, #ffffff 0%, #f0fdf4 100%)'
                  : '#ffffff',
                borderRadius: '18px',
                border: app.is_active
                  ? (theme) => `1.5px solid ${theme.palette.primary.light || '#86efac'}`
                  : '1.5px solid #e2e8f0',
                boxShadow: app.is_active
                  ? (theme) => `0 4px 20px ${theme.palette.primary.main}1a`
                  : '0 2px 12px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.22s cubic-bezier(0.34,1.4,0.64,1)',
                animation: `fadeSlideUp 0.35s ease both`,
                animationDelay: `${index * 0.04}s`,
                '@keyframes fadeSlideUp': {
                  from: { opacity: 0, transform: 'translateY(14px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                '&:hover': {
                  transform: 'translateY(-5px) scale(1.01)',
                  boxShadow: app.is_active
                    ? (theme) => `0 16px 40px ${theme.palette.primary.main}33`
                    : '0 14px 38px rgba(0,0,0,0.12)',
                  borderColor: app.is_active
                    ? (theme) => theme.palette.primary.light || '#4ade80'
                    : '#94a3b8',
                },
              }}
            >
              {/* Top accent bar */}
              <Box
                sx={{
                  height: '4px',
                  background: app.is_active
                    ? undefined
                    : `linear-gradient(90deg, hsl(${hue},50%,58%), hsl(${(hue + 40) % 360},50%,68%))`,
                  bgcolor: app.is_active ? 'primary.main' : undefined,
                }}
              />

              <Box sx={{ padding: '18px 20px 20px' }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 44, height: 44, borderRadius: '12px',
                        background: `linear-gradient(135deg, hsl(${hue},55%,44%), hsl(${(hue + 40) % 360},55%,54%))`,
                        fontSize: '0.82rem', fontWeight: 800, color: '#fff', flexShrink: 0,
                      }}
                    >
                      {getInitials(app.app_name)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.93rem', color: '#1e293b', lineHeight: 1.25 }}>
                        {app.app_name}
                      </Typography>
                      {app.company_name && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
                          {app.company_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Chip
                    label={app.is_active ? '● Live' : '● Offline'}
                    size="small"
                    sx={
                      app.is_active
                        ? {
                            bgcolor: 'primary.main',
                            color: '#fff', fontWeight: 700, fontSize: '0.7rem',
                            height: 24, borderRadius: '8px', flexShrink: 0,
                          }
                        : {
                            background: '#fff0f0', color: '#e53935',
                            border: '1px solid #fecaca',
                            fontWeight: 700, fontSize: '0.7rem',
                            height: 24, borderRadius: '8px', flexShrink: 0,
                          }
                    }
                  />
                </Box>

                <Box sx={{ height: '1px', background: '#f1f5f9', mb: 1.5 }} />

                {/* Info */}
                {[
                  { icon: <PhoneIphoneIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'Phone', value: app.wa_phone_number, mono: false },
                  app.waba_id && { icon: <FingerprintIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'WABA', value: app.waba_id, mono: true },
                  app.limit !== 'N/A' && { icon: <SignalCellularAltIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'Limit', value: app.limit, mono: false },
                  app.source && { icon: <BusinessIcon sx={{ fontSize: 13, color: '#94a3b8' }} />, label: 'Source', value: app.source, mono: false },
                ]
                  .filter(Boolean)
                  .map((row: any, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '5px' }}>
                      {row.icon}
                      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', minWidth: 38 }}>
                        {row.label}
                      </Typography>
                      <Typography sx={{ fontSize: row.mono ? '0.76rem' : '0.82rem', color: '#475569', fontWeight: 500, fontFamily: row.mono ? 'monospace' : 'inherit' }}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}

                {/* Footer */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  {!app.is_active ? (
                    <Button
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleGoLiveClick(app); }}
                      variant="contained"
                      color="primary"
                      sx={{
                        borderRadius: '9px', fontSize: '0.78rem',
                        fontWeight: 700, textTransform: 'none',
                        padding: '5px 14px',
                        boxShadow: (theme) => `0 2px 10px ${theme.palette.primary.main}44`,
                        '&:hover': {
                          boxShadow: (theme) => `0 4px 18px ${theme.palette.primary.main}66`,
                        },
                      }}
                    >
                      🚀 Go Live
                    </Button>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        bgcolor: (theme) => `${theme.palette.primary.main}15`,
                        borderRadius: '8px',
                        padding: '4px 11px',
                        border: (theme) => `1px solid ${theme.palette.primary.main}44`,
                      }}
                    >
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}33` }} />
                      <Typography sx={{ fontSize: '0.74rem', color: 'primary.main', fontWeight: 700 }}>Active</Typography>
                    </Box>
                  )}

                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
                    onClick={(e) => { e.stopPropagation(); handleCardClick(app); }}
                    sx={{
                      color: '#64748b', fontSize: '0.77rem', fontWeight: 600,
                      textTransform: 'none', padding: '4px 11px',
                      borderRadius: '8px', background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      '&:hover': { background: '#f1f5f9', color: '#334155' },
                    }}
                  >
                    Details
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── WHATSAPP MODAL ── */}
      {showWhatsAppModal && (
        <WhatsAppApiModal appName={selectedApp?.app_name} onClose={handleCloseWhatsAppModal} />
      )}

      {/* ── CREATE APP MODAL ── */}
      <Modal open={isCreateAppModalOpen} onClose={() => { setIsCreateAppModalOpen(false); setAppName(''); }}>
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 320, sm: 440 },
            bgcolor: '#fff', borderRadius: '20px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.16)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ bgcolor: 'primary.main', padding: '22px 26px 20px' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <WhatsAppIcon sx={{ color: '#fff', fontSize: 26 }} />
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>
                Create New App
              </Typography>
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', mt: 0.5 }}>
              Define a unique name for your WhatsApp Messaging App
            </Typography>
          </Box>

          <Box sx={{ padding: '24px 28px' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', mb: 0.8, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
              App Name
            </Typography>
            <TextField
              placeholder="e.g. Machadalo Support Bot"
              size="small"
              fullWidth
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateApp()}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px', fontSize: '0.92rem',
                  '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1.5px' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
            />
            <Box display="flex" gap={1.5}>
              <Button fullWidth variant="outlined"
                onClick={() => { setIsCreateAppModalOpen(false); setAppName(''); }}
                sx={{
                  borderRadius: '12px', fontWeight: 700, textTransform: 'none',
                  borderColor: '#e2e8f0', color: '#64748b',
                  '&:hover': { borderColor: '#94a3b8', background: '#f8fafc' },
                }}
              >
                Cancel
              </Button>
              <Button fullWidth variant="contained" color="primary" onClick={handleCreateApp}
                sx={{
                  borderRadius: '12px', fontWeight: 700, textTransform: 'none',
                  boxShadow: (theme) => `0 4px 14px ${theme.palette.primary.main}44`,
                }}
              >
                Create App
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AppList;